import { ApiResponse } from '@/types/services';
import { BrandOrder, BrandSelectionUpdate } from '@/types/services';
import { baseApi } from './base-api';

export type GetBrandOrderResponse = ApiResponse<BrandOrder[]>;

export type BrandsContext = 'region' | 'province';

export type UpdateBrandSelectionArgs = {
  brandUpdates: BrandSelectionUpdate[];
  context: BrandsContext;
};
export const brandsOrderApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getBrandsOrder: builder.query<BrandOrder[], void>({
      query: () => '/brand-order',
      providesTags: ['BrandOrder'],
      transformResponse: (response: GetBrandOrderResponse): BrandOrder[] => {
        return response.data || [];
      }
    }),
    updateBrandSelection: builder.mutation<{ success: boolean; message: string }, UpdateBrandSelectionArgs>({
      query: ({ brandUpdates }) => ({
        url: '/brand-order',
        method: 'PUT',
        body: { brandUpdates }
      }),
      async onQueryStarted({ brandUpdates }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          brandsOrderApi.util.updateQueryData('getBrandsOrder', undefined, draft => {
            brandUpdates.forEach(update => {
              const brand = draft.find(b => b.id === update.brandId);
              if (brand) {
                brand.isSelected = update.isSelected;
                brand.displayOrder = update.displayOrder ?? null;
              }
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { context }) =>
        context === 'province'
          ? ['ProvinceSummary', 'ProvinceOverallSummary']
          : ['Summary', 'RegionalSummary']
    }),
    resetBrandOrders: builder.mutation<{ success: boolean; message: string }, BrandsContext>({
      query: () => ({
        url: '/brand-order',
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, context) =>
        context === 'province'
          ? ['BrandOrder', 'ProvinceSummary', 'ProvinceOverallSummary']
          : ['BrandOrder', 'Summary', 'RegionalSummary']
    }),

    getRegionalBrandsOrder: builder.query<BrandOrder[], void>({
      query: () => '/brand-order-regional',
      providesTags: ['RegionalBrandOrder'],
      transformResponse: (response: GetBrandOrderResponse): BrandOrder[] => {
        return response.data || [];
      }
    }),
    updateRegionalBrandSelection: builder.mutation<{ success: boolean; message: string }, UpdateBrandSelectionArgs>({
      query: ({ brandUpdates }) => ({
        url: '/brand-order-regional',
        method: 'PUT',
        body: { brandUpdates }
      }),
      async onQueryStarted({ brandUpdates }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          brandsOrderApi.util.updateQueryData('getRegionalBrandsOrder', undefined, draft => {
            brandUpdates.forEach(update => {
              const brand = draft.find(b => b.id === update.brandId);
              if (brand) {
                brand.isSelected = update.isSelected;
                brand.displayOrder = update.displayOrder ?? null;
              }
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { context }) =>
        context === 'province'
          ? ['ProvinceSummary', 'ProvinceOverallSummary']
          : ['Summary', 'RegionalSummary']
    }),
    resetRegionalBrandOrders: builder.mutation<{ success: boolean; message: string }, BrandsContext>({
      query: () => ({
        url: '/brand-order-regional',
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, context) =>
        context === 'province'
          ? ['RegionalBrandOrder', 'ProvinceSummary', 'ProvinceOverallSummary']
          : ['RegionalBrandOrder', 'Summary', 'RegionalSummary']
    })
  })
});
export const {
  useGetBrandsOrderQuery,
  useUpdateBrandSelectionMutation,
  useResetBrandOrdersMutation,
  useGetRegionalBrandsOrderQuery,
  useUpdateRegionalBrandSelectionMutation,
  useResetRegionalBrandOrdersMutation
} = brandsOrderApi;
